import type { TableDefinition, TablePart } from '../engine/tables/definition';

export type LightingPreset = 'arcade' | 'neon' | 'warm' | 'moonlight' | 'ice' | 'lava';
export type PlayfieldPattern =
  | 'rings'
  | 'circuit'
  | 'stars'
  | 'rays'
  | 'checker'
  | 'waves'
  | 'hex'
  | 'hazard'
  | 'minimal';

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  tags: string[];
  palette: {
    playfield: string;
    playfieldSecondary: string;
    cabinet: string;
    cabinetEdge: string;
    accent: string;
    secondary: string;
    metal: string;
    rubber: string;
    flipper: string;
    background: string;
  };
  pattern: PlayfieldPattern;
  soundPackId: string;
  lighting: LightingPreset;
}

export interface SoundPack {
  id: string;
  name: string;
  description: string;
  character: 'mechanical' | 'electronic' | 'fantasy' | 'spooky' | 'casino' | 'cartoon';
  pitch: number;
  brightness: number;
  decay: number;
}

export const SOUND_PACKS: SoundPack[] = [
  { id: 'classic-mechanical', name: 'Classic Mechanical', description: 'Dry coils, relays and arcade chimes.', character: 'mechanical', pitch: 1, brightness: 0.72, decay: 0.6 },
  { id: 'solid-state-80s', name: 'Solid State 80s', description: 'Punchy synthesized hits and bright bleeps.', character: 'electronic', pitch: 1.08, brightness: 0.92, decay: 0.45 },
  { id: 'neon-future', name: 'Neon Future', description: 'Clean sci-fi zaps, pulses and energy sweeps.', character: 'electronic', pitch: 1.18, brightness: 1, decay: 0.48 },
  { id: 'haunted-arcade', name: 'Haunted Arcade', description: 'Low metallic knocks and eerie resonances.', character: 'spooky', pitch: 0.78, brightness: 0.5, decay: 0.9 },
  { id: 'royal-fantasy', name: 'Royal Fantasy', description: 'Bell-like impacts and magical sparkle.', character: 'fantasy', pitch: 1.2, brightness: 0.82, decay: 1.05 },
  { id: 'casino-night', name: 'Casino Night', description: 'Crisp bells, coin-like pings and jackpot flourishes.', character: 'casino', pitch: 1.12, brightness: 0.95, decay: 0.65 },
  { id: 'cartoon-chaos', name: 'Cartoon Chaos', description: 'Bouncy, playful and exaggerated pinball sounds.', character: 'cartoon', pitch: 1.3, brightness: 0.88, decay: 0.42 },
  { id: 'deep-space', name: 'Deep Space', description: 'Wide low pulses and distant electronic impacts.', character: 'electronic', pitch: 0.72, brightness: 0.58, decay: 1.15 },
];

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'classic-arcade',
    name: 'Classic Arcade',
    description: 'Warm inserts, dark lacquer and chrome hardware.',
    tags: ['classic', 'arcade', 'starter'],
    palette: { playfield: '#17324b', playfieldSecondary: '#224f6e', cabinet: '#101018', cabinetEdge: '#2a2a38', accent: '#ffd166', secondary: '#ef476f', metal: '#d9e1e8', rubber: '#f3edda', flipper: '#ffd166', background: '#06070c' },
    pattern: 'rings', soundPackId: 'classic-mechanical', lighting: 'arcade',
  },
  {
    id: 'neon-reactor', name: 'Neon Reactor', description: 'Cyan/magenta energy with a deep navy playfield.', tags: ['neon', 'cyber', 'sci-fi'],
    palette: { playfield: '#102b55', playfieldSecondary: '#163a6b', cabinet: '#070a12', cabinetEdge: '#151d2d', accent: '#53e4ff', secondary: '#ff4fa3', metal: '#d8e4ef', rubber: '#f3f0da', flipper: '#ffc857', background: '#05070d' },
    pattern: 'circuit', soundPackId: 'neon-future', lighting: 'neon',
  },
  {
    id: 'cosmic-voyage', name: 'Cosmic Voyage', description: 'Midnight space, star lanes and violet energy.', tags: ['space', 'stars', 'sci-fi'],
    palette: { playfield: '#090d2b', playfieldSecondary: '#161d53', cabinet: '#03040d', cabinetEdge: '#16172a', accent: '#8ef9f3', secondary: '#9b5de5', metal: '#becbe8', rubber: '#eef4ff', flipper: '#f9c74f', background: '#010208' },
    pattern: 'stars', soundPackId: 'deep-space', lighting: 'moonlight',
  },
  {
    id: 'haunted-mansion', name: 'Haunted Mansion', description: 'Dark emerald wood, ghostly violet and candle gold.', tags: ['spooky', 'gothic', 'night'],
    palette: { playfield: '#102820', playfieldSecondary: '#1e4634', cabinet: '#090b09', cabinetEdge: '#2b211b', accent: '#b8f2a6', secondary: '#9d4edd', metal: '#b7b2a8', rubber: '#e8e0ca', flipper: '#e9c46a', background: '#030604' },
    pattern: 'waves', soundPackId: 'haunted-arcade', lighting: 'warm',
  },
  {
    id: 'ocean-depths', name: 'Ocean Depths', description: 'Deep blue water, turquoise lanes and coral highlights.', tags: ['ocean', 'water', 'bright'],
    palette: { playfield: '#063a5b', playfieldSecondary: '#087f8c', cabinet: '#041b2d', cabinetEdge: '#0a5064', accent: '#4cc9f0', secondary: '#ff6b6b', metal: '#cfe8ef', rubber: '#f5f4e8', flipper: '#ffd166', background: '#02111c' },
    pattern: 'waves', soundPackId: 'neon-future', lighting: 'neon',
  },
  {
    id: 'volcanic-core', name: 'Volcanic Core', description: 'Charcoal playfield with molten orange and red energy.', tags: ['lava', 'fire', 'hard'],
    palette: { playfield: '#261515', playfieldSecondary: '#4a1c16', cabinet: '#0c0908', cabinetEdge: '#2c1712', accent: '#ff9f1c', secondary: '#ff3b30', metal: '#c4b5a5', rubber: '#efe2cc', flipper: '#ffd166', background: '#080302' },
    pattern: 'hazard', soundPackId: 'classic-mechanical', lighting: 'lava',
  },
  {
    id: 'glacier-run', name: 'Glacier Run', description: 'Icy blue glass, white rubber and sharp cyan inserts.', tags: ['ice', 'clean', 'bright'],
    palette: { playfield: '#d7f3ff', playfieldSecondary: '#8fd7ee', cabinet: '#132235', cabinetEdge: '#28445d', accent: '#00b4d8', secondary: '#4361ee', metal: '#f4fbff', rubber: '#ffffff', flipper: '#f9c74f', background: '#07131f' },
    pattern: 'hex', soundPackId: 'solid-state-80s', lighting: 'ice',
  },
  {
    id: 'jungle-ruins', name: 'Jungle Ruins', description: 'Dense greens, gold relics and stone-colored hardware.', tags: ['jungle', 'adventure', 'temple'],
    palette: { playfield: '#1d3b24', playfieldSecondary: '#315a31', cabinet: '#10150f', cabinetEdge: '#3c3324', accent: '#f4d35e', secondary: '#ee964b', metal: '#b7aa8d', rubber: '#ece2c6', flipper: '#f4d35e', background: '#081008' },
    pattern: 'rays', soundPackId: 'royal-fantasy', lighting: 'warm',
  },
  {
    id: 'candy-chaos', name: 'Candy Chaos', description: 'Playful pastel color with loud pop-art inserts.', tags: ['candy', 'fun', 'easy'],
    palette: { playfield: '#59345f', playfieldSecondary: '#8f4e8f', cabinet: '#251329', cabinetEdge: '#6d3b72', accent: '#ff70a6', secondary: '#70d6ff', metal: '#f8e6ff', rubber: '#fff7e8', flipper: '#ffdb66', background: '#160a19' },
    pattern: 'checker', soundPackId: 'cartoon-chaos', lighting: 'neon',
  },
  {
    id: 'steampunk-lab', name: 'Steampunk Lab', description: 'Oxidized copper, brass rails and warm industrial glow.', tags: ['steampunk', 'industrial', 'brass'],
    palette: { playfield: '#2e261f', playfieldSecondary: '#59412c', cabinet: '#18120d', cabinetEdge: '#4a3323', accent: '#d4a373', secondary: '#84a98c', metal: '#b17b49', rubber: '#e9dcc4', flipper: '#e9c46a', background: '#0e0a07' },
    pattern: 'circuit', soundPackId: 'classic-mechanical', lighting: 'warm',
  },
  {
    id: 'casino-night', name: 'Casino Night', description: 'Black lacquer, ruby red, emerald green and jackpot gold.', tags: ['casino', 'gold', 'night'],
    palette: { playfield: '#10251d', playfieldSecondary: '#173c2e', cabinet: '#070707', cabinetEdge: '#241b12', accent: '#ffd166', secondary: '#d62828', metal: '#e7c46a', rubber: '#f7ead1', flipper: '#ffd166', background: '#030403' },
    pattern: 'rays', soundPackId: 'casino-night', lighting: 'warm',
  },
  {
    id: 'synthwave-drive', name: 'Synthwave Drive', description: 'Purple sunset, electric cyan and retro grid energy.', tags: ['retro', 'synthwave', '80s'],
    palette: { playfield: '#24104f', playfieldSecondary: '#4a1b6f', cabinet: '#090417', cabinetEdge: '#24133b', accent: '#00f5d4', secondary: '#f15bb5', metal: '#c9d7ff', rubber: '#f7f2ff', flipper: '#fee440', background: '#05010c' },
    pattern: 'checker', soundPackId: 'solid-state-80s', lighting: 'neon',
  },
  {
    id: 'royal-quest', name: 'Royal Quest', description: 'Deep blue velvet, royal purple and polished gold.', tags: ['fantasy', 'royal', 'quest'],
    palette: { playfield: '#18245a', playfieldSecondary: '#2d3e86', cabinet: '#090d20', cabinetEdge: '#2a2248', accent: '#f4d35e', secondary: '#8f6cff', metal: '#d7b65d', rubber: '#f3ead4', flipper: '#f4d35e', background: '#050713' },
    pattern: 'rays', soundPackId: 'royal-fantasy', lighting: 'warm',
  },
  {
    id: 'blueprint-lab', name: 'Blueprint Lab', description: 'Technical blueprint lines and clean engineering contrast.', tags: ['technical', 'minimal', 'builder'],
    palette: { playfield: '#0b315d', playfieldSecondary: '#155292', cabinet: '#071a2e', cabinetEdge: '#163f63', accent: '#90e0ef', secondary: '#ffffff', metal: '#d9edf5', rubber: '#ffffff', flipper: '#f9c74f', background: '#04101e' },
    pattern: 'minimal', soundPackId: 'classic-mechanical', lighting: 'arcade',
  },
  {
    id: 'desert-rally', name: 'Desert Rally', description: 'Sand, rust, racing yellow and heat-haze orange.', tags: ['desert', 'racing', 'warm'],
    palette: { playfield: '#5e402d', playfieldSecondary: '#8a6042', cabinet: '#20150f', cabinetEdge: '#533523', accent: '#ffb703', secondary: '#fb8500', metal: '#c8b89d', rubber: '#f1e0bc', flipper: '#ffb703', background: '#130c08' },
    pattern: 'hazard', soundPackId: 'classic-mechanical', lighting: 'warm',
  },
  {
    id: 'quantum-circuit', name: 'Quantum Circuit', description: 'Black PCB styling with green traces and electric violet.', tags: ['circuit', 'tech', 'hard'],
    palette: { playfield: '#061b16', playfieldSecondary: '#0c382d', cabinet: '#020907', cabinetEdge: '#0a2b22', accent: '#80ff72', secondary: '#b517ff', metal: '#a8c9bd', rubber: '#e8f5ea', flipper: '#80ff72', background: '#010604' },
    pattern: 'circuit', soundPackId: 'neon-future', lighting: 'neon',
  },
];

export const PLAYFIELD_PATTERNS: Array<{ id: PlayfieldPattern; name: string }> = [
  { id: 'rings', name: 'Arcade Rings' },
  { id: 'circuit', name: 'Circuit Traces' },
  { id: 'stars', name: 'Star Field' },
  { id: 'rays', name: 'Radial Rays' },
  { id: 'checker', name: 'Retro Grid' },
  { id: 'waves', name: 'Flowing Waves' },
  { id: 'hex', name: 'Hex Grid' },
  { id: 'hazard', name: 'Hazard Stripes' },
  { id: 'minimal', name: 'Minimal' },
];

export function getThemePreset(id: string | undefined): ThemePreset {
  return THEME_PRESETS.find((preset) => preset.id === id) ?? THEME_PRESETS[0]!;
}

export function getSoundPack(id: string | undefined): SoundPack {
  return SOUND_PACKS.find((pack) => pack.id === id) ?? SOUND_PACKS[0]!;
}

function recolorPart(part: TablePart, preset: ThemePreset, index: number): TablePart {
  const { palette } = preset;
  switch (part.kind) {
    case 'flipper-left':
    case 'flipper-right':
      return { ...part, color: palette.flipper };
    case 'bumper':
      return { ...part, color: index % 2 === 0 ? palette.accent : palette.secondary };
    case 'slingshot':
      return { ...part, color: palette.secondary };
    case 'insert':
    case 'rollover':
      return { ...part, color: index % 3 === 0 ? palette.secondary : palette.accent };
    case 'target':
      return { ...part, color: palette.accent };
    case 'guide-rail':
    case 'wall':
    case 'post':
      return { ...part, color: palette.metal };
    case 'ramp':
      return { ...part, color: palette.playfieldSecondary };
    default:
      return part;
  }
}

export function applyThemePreset(table: TableDefinition, presetId: string): TableDefinition {
  const preset = getThemePreset(presetId);
  return {
    ...table,
    theme: {
      ...table.theme,
      presetId: preset.id,
      accentColor: preset.palette.accent,
      secondaryColor: preset.palette.secondary,
      playfieldColor: preset.palette.playfield,
      playfieldSecondaryColor: preset.palette.playfieldSecondary,
      cabinetColor: preset.palette.cabinet,
      cabinetEdgeColor: preset.palette.cabinetEdge,
      metalColor: preset.palette.metal,
      rubberColor: preset.palette.rubber,
      flipperColor: preset.palette.flipper,
      backgroundColor: preset.palette.background,
      playfieldPattern: preset.pattern,
      soundPackId: preset.soundPackId,
      lightingPreset: preset.lighting,
    },
    parts: table.parts.map((part, index) => recolorPart(part, preset, index)),
  };
}
